import { FunctionComponent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Flex,
  FormOtp,
  TextComponent,
} from '@monorepo/react-components';
import { useLoading } from '@monorepo/react-hooks';
import {
  FailedMailOtpCodeReason,
  MAIL_OTP_MAX_RETRIES,
  ReceivedMailState,
} from '@monorepo/types';
import { useStore } from '../../helpers/use-store';

import './mail-confirmation.scss';

interface Props {
  sourceId: string;
  setReceivedMailState: React.Dispatch<
    React.SetStateAction<ReceivedMailState | undefined>
  >;
  getOtpStatus: () => Promise<void>;
}

export const MailOtpStage: FunctionComponent<Props> = ({
  sourceId,
  setReceivedMailState,
  getOtpStatus,
}) => {
  const [tries, setTries] = useState(0);
  const form = useForm<{ code: string }>({ mode: 'onChange' });
  const { isLoading, setLoadingState } = useLoading();

  const {
    dataStore: { mailOtpStore },
  } = useStore();
  const { t } = useTranslation(['mailConfirmation', 'common']);

  const code = form.watch('code');

  const handleClick = async ({ code }: { code: string }) => {
    try {
      setLoadingState('Loading');
      const response = await mailOtpStore.verifyMailOtp(sourceId, code);

      if (response.verified) {
        setReceivedMailState(ReceivedMailState.VERIFIED);
      } else {
        if (response.reason === FailedMailOtpCodeReason.INVALID_CODE) {
          setTries(response.tries || 0);
        } else if (response.reason === FailedMailOtpCodeReason.EXPIRED_CODE) {
          await getOtpStatus();
          setTries(0);
        }
      }

      setLoadingState('Loaded');
    } catch {
      setLoadingState('Error');
      setReceivedMailState(ReceivedMailState.ERROR);
    }
  };

  const isFullCode = code?.length === 6;

  useEffect(() => {
    const handleEnterKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && isFullCode) {
        form.handleSubmit(handleClick)();
      }
    };

    window.addEventListener('keydown', handleEnterKeyDown);
    return () => {
      window.removeEventListener('keydown', handleEnterKeyDown);
    };
  }, [code]);

  return (
    <Flex vertical align="center" gap={62} className="mail-otp-stage">
      <Flex vertical align="center">
        <TextComponent level={2} weight={600} className="title">
          {t('mailConfirmation:code_verification')}
        </TextComponent>
        <TextComponent weight={400} level={7}>
          {t('mailConfirmation:enter_the_code_we_sent_to_your_mail')}
        </TextComponent>
      </Flex>

      <FormOtp
        form={form}
        controllerProps={{
          name: 'code',
        }}
        onKeyDown={(event) => {
          const { key } = event;

          if (!/[0-9]/.test(key) && key !== 'Backspace') {
            event.preventDefault();
          }
        }}
      />
      {tries > 0 && (
        <TextComponent level={6} weight={400} type="danger">
          {t('mailConfirmation:mail_opt_max_retries', {
            count: tries,
            tries,
            maxRetries: MAIL_OTP_MAX_RETRIES,
          })}
        </TextComponent>
      )}
      <Flex gap={39}>
        <Button
          type="primary"
          loading={isLoading}
          disabled={!isFullCode}
          onClick={form.handleSubmit(handleClick)}
        >
          {t('common:continue')}
        </Button>
      </Flex>
    </Flex>
  );
};
